import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { 
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  FunnelIcon,
  EyeIcon,
  CalendarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

const AdminCommissionHistory = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filter, setFilter] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    let status = sp.get('status') || '';
    if (status === 'pending') status = 'calculated'; // 레거시: 입금대기 = 미납(calculated)
    return {
      status,
      contractor_id: sp.get('contractor_id') || '',
      // 상태가 지정되면(대시보드에서 '미납' 클릭 등) 기간 기본을 '전체'로 → 1개월 지난 미납도 노출
      period: sp.get('period') || (status ? 'all' : 'month'),
      fee_type: sp.get('fee_type') || ''
    };
  });
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [memoData, setMemoData] = useState({
    contractorNotes: '',
    adminPrivateNotes: ''
  });
  const [showMonthlyModal, setShowMonthlyModal] = useState(false);
  const [selectedMonthlyContractor, setSelectedMonthlyContractor] = useState(null);

  // 수수료 내역 조회
  const { data: commissionData, isLoading } = useQuery(
    ['admin-commission-history', filter, page, pageSize],
    () => api.get('/admin/commission-history', { params: { ...filter, page, limit: pageSize } }).then(res => res.data),
    { keepPreviousData: true }
  );

  // 소장 목록 조회
  const { data: contractorsData } = useQuery(
    'contractors-list',
    () => api.get('/admin/contractors').then(res => res.data)
  );
  
  const contractors = contractorsData?.contractors || [];

  // 수수료 상태 업데이트
  const updateStatusMutation = useMutation(
    ({ id, status, contractorNotes, adminPrivateNotes }) => 
      api.patch(`/admin/commission-history/${id}`, { status, contractorNotes, adminPrivateNotes }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-commission-history');
        toast.success('상태가 업데이트되었습니다.');
        setShowMemoModal(false);
        setSelectedCommission(null);
        setMemoData({ contractorNotes: '', adminPrivateNotes: '' });
      }
    }
  );

  // 월정액 수수료 생성
  const createMonthlyCommissionMutation = useMutation(
    (contractorId) => api.post(`/admin/contractors/${contractorId}/monthly-commission`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-commission-history');
        toast.success('월정액 수수료가 생성되었습니다.');
        setShowMonthlyModal(false);
        setSelectedMonthlyContractor(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '월정액 수수료 생성에 실패했습니다.');
      }
    }
  );

  const formatCurrency = (amount) => {
    if (!amount) return '0원';
    const billion = Math.floor(amount / 100000000);
    const million = Math.floor((amount % 100000000) / 10000);
    
    if (billion > 0) {
      return `${billion}억${million > 0 ? ` ${million}만원` : '원'}`;
    } else if (million > 0) {
      return `${million}만원`;
    } else {
      return new Intl.NumberFormat('ko-KR').format(amount) + '원';
    }
  };

  const getStatusBadge = (status, paymentRequestedAt, paymentConfirmedAt) => {
    if (paymentConfirmedAt) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">납부완료</span>;
    } else if (paymentRequestedAt) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">확인대기</span>;
    } else if (status === 'calculated') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">미납</span>;
    }
    return null;
  };

  const handlePaymentConfirm = (commission, isCancel = false) => {
    setSelectedCommission(commission);
    setMemoData({ 
      contractorNotes: isCancel ? '입금 확인이 취소되었습니다.' : '', 
      adminPrivateNotes: '' 
    });
    setShowMemoModal(true);
  };

  const handleMemoSubmit = (status) => {
    updateStatusMutation.mutate({
      id: selectedCommission.id,
      status: status,
      contractorNotes: memoData.contractorNotes,
      adminPrivateNotes: memoData.adminPrivateNotes
    });
  };

  return (
    <div>
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">수수료 납부 관리</h1>
          <p className="mt-2 text-gray-600">
            소장별 수수료 납부 현황을 관리하고 확인합니다.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">표시 개수:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value={25}>25개</option>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
            <option value={200}>200개</option>
          </select>
        </div>
      </div>

      {/* 요약 정보 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">이번 달 납부액</p>
              <p className="text-2xl font-bold">{formatCurrency(commissionData?.summary?.monthly_total || 0)}</p>
            </div>
            <CurrencyDollarIcon className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">미납 건수</p>
              <p className="text-2xl font-bold">{commissionData?.summary?.calculated_count || 0}건</p>
            </div>
            <ExclamationCircleIcon className="h-8 w-8 text-red-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">입금 신고</p>
              <p className="text-2xl font-bold">{commissionData?.summary?.pending_count || 0}건</p>
            </div>
            <ClockIcon className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">납부 완료</p>
              <p className="text-2xl font-bold">{commissionData?.summary?.paid_count || 0}건</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* 월정액 소장 현황 */}
      {commissionData?.monthlyContractors?.length > 0 && (
        <div className="bg-yellow-50 rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            월정액 소장 현황
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {commissionData.monthlyContractors.map((contractor) => (
              <div key={contractor.id} className="bg-white rounded-lg p-4 border border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{contractor.name}</h3>
                  <span className="text-sm text-gray-500">{contractor.company_name}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">월 정액:</span>
                    <span className="font-medium">{formatCurrency(contractor.monthly_amount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">납부일:</span>
                    <span className="font-medium">매월 {contractor.payment_day}일</span>
                  </div>
                  <div className="pt-2 border-t space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-red-600">미납:</span>
                      <span className="font-medium text-red-600">{contractor.unpaid_count || 0}건</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-yellow-600">대기:</span>
                      <span className="font-medium text-yellow-600">{contractor.pending_count || 0}건</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-green-600">완료:</span>
                      <span className="font-medium text-green-600">{contractor.paid_count || 0}건</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedMonthlyContractor(contractor);
                      setShowMonthlyModal(true);
                    }}
                    className="mt-3 w-full px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    이번 달 수수료 생성
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">기간</label>
            <select
              value={filter.period}
              onChange={(e) => setFilter({ ...filter, period: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="week">최근 1주일</option>
              <option value="month">최근 1개월</option>
              <option value="quarter">최근 3개월</option>
              <option value="year">최근 1년</option>
              <option value="all">전체</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">전체</option>
              <option value="calculated">미납</option>
              <option value="paid">납부완료</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">소장</label>
            <select
              value={filter.contractor_id}
              onChange={(e) => setFilter({ ...filter, contractor_id: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">전체</option>
              {contractors?.map(contractor => (
                <option key={contractor.id} value={contractor.id}>
                  {contractor.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">수수료 타입</label>
            <select
              value={filter.fee_type}
              onChange={(e) => setFilter({ ...filter, fee_type: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">전체</option>
              <option value="commission">프로젝트별</option>
              <option value="monthly">월정액</option>
            </select>
          </div>
        </div>
      </div>

      {/* 수수료 내역 - 모바일/태블릿 카드 레이아웃 */}
      <div className="block lg:hidden space-y-4">
        {isLoading ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : commissionData?.history?.length > 0 ? (
          commissionData.history.map((commission) => (
            <div key={commission.id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 sm:p-6">
                {/* 헤더 */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      commission.fee_type === 'monthly' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {commission.fee_type === 'monthly' ? '월정액' : '프로젝트별'}
                    </span>
                    {getStatusBadge(commission.status, commission.payment_requested_at, commission.payment_confirmed_at)}
                  </div>
                </div>

                {/* 소장 정보 */}
                <div className="mb-3">
                  <div className="text-base font-semibold text-gray-900">{commission.contractor_name}</div>
                  <div className="text-sm text-gray-600">{commission.construction_type}</div>
                </div>

                {/* 프로젝트 정보 */}
                {commission.project_id && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700">프로젝트</div>
                    <div className="text-sm text-gray-900">{commission.project_title}</div>
                    <div className="text-xs text-gray-500">#{commission.project_id}</div>
                  </div>
                )}

                {/* 금액 정보 */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <div className="text-xs text-gray-500">계약금액</div>
                    <div className="text-sm font-medium text-gray-900">{formatCurrency(commission.contract_amount)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">수수료</div>
                    <div className="text-sm font-bold text-blue-600">{formatCurrency(commission.commission_amount)}</div>
                    {commission.commission_rate && (
                      <div className="text-xs text-gray-500">{commission.commission_rate}%</div>
                    )}
                  </div>
                </div>

                {/* 날짜 정보 */}
                <div className="mb-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">계산일:</span>
                    <span className="text-gray-700">{new Date(commission.calculated_at).toLocaleDateString()}</span>
                  </div>
                  {commission.payment_requested_at && (
                    <div className="flex justify-between text-xs">
                      <span className="text-yellow-600">입금신고:</span>
                      <span className="text-yellow-700">{new Date(commission.payment_requested_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {commission.payment_confirmed_at && (
                    <div className="flex justify-between text-xs">
                      <span className="text-green-600">확인완료:</span>
                      <span className="text-green-700">{new Date(commission.payment_confirmed_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {/* 메모 */}
                {commission.admin_private_notes && (
                  <div className="mb-3 p-2 bg-gray-100 rounded text-xs text-gray-700">
                    <span className="font-medium">관리자 메모:</span> {commission.admin_private_notes}
                  </div>
                )}

                {/* 액션 버튼 */}
                <div className="border-t pt-3">
                  {!commission.payment_confirmed_at ? (
                    <button
                      onClick={() => handlePaymentConfirm(commission)}
                      className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      납부 확인
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePaymentConfirm(commission, true)}
                      className="w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
                    >
                      확인 취소
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            수수료 내역이 없습니다.
          </div>
        )}
      </div>

      {/* 수수료 내역 - PC 테이블 레이아웃 */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                타입
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                프로젝트
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                소장
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                공사유형
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                계약금액
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                수수료
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                날짜
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                </td>
              </tr>
            ) : commissionData?.history?.length > 0 ? (
              commissionData.history.map((commission) => (
                <tr key={commission.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      commission.fee_type === 'monthly' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {commission.fee_type === 'monthly' ? '월정액' : '프로젝트별'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {commission.project_id ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={commission.project_title}>
                          {commission.project_title}
                        </div>
                        <div className="text-xs text-gray-500">#{commission.project_id}</div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">-</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{commission.contractor_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 truncate max-w-[120px]" title={commission.construction_type}>
                      {commission.construction_type}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(commission.contract_amount)}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-blue-600">{formatCurrency(commission.commission_amount)}</div>
                    {commission.commission_rate && (
                      <div className="text-xs text-gray-500">{commission.commission_rate}%</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(commission.status, commission.payment_requested_at, commission.payment_confirmed_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-900">{new Date(commission.calculated_at).toLocaleDateString()}</div>
                    {commission.payment_requested_at && (
                      <div className="text-xs text-yellow-600">
                        신고: {new Date(commission.payment_requested_at).toLocaleDateString()}
                      </div>
                    )}
                    {commission.payment_confirmed_at && (
                      <div className="text-xs text-green-600">
                        확인: {new Date(commission.payment_confirmed_at).toLocaleDateString()}
                      </div>
                    )}
                    {commission.admin_private_notes && (
                      <div className="text-xs text-gray-500 mt-1" title={commission.admin_private_notes}>
                        <span className="font-medium">메모</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    {!commission.payment_confirmed_at ? (
                      <button
                        onClick={() => handlePaymentConfirm(commission)}
                        className="px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 transition-colors"
                      >
                        납부확인
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePaymentConfirm(commission, true)}
                        className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors"
                      >
                        확인취소
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                  수수료 내역이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {commissionData && commissionData.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <button
              onClick={() => setPage(Math.min(commissionData.pages, page + 1))}
              disabled={page === commissionData.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                전체 <span className="font-medium">{commissionData.total}</span>개 중{' '}
                <span className="font-medium">{((page - 1) * pageSize) + 1}</span> -{' '}
                <span className="font-medium">{Math.min(page * pageSize, commissionData.total)}</span> 표시
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">이전</span>
                  ◀
                </button>
                
                {(() => {
                  const totalPages = commissionData.pages;
                  const maxButtons = 7;
                  let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  if (endPage - startPage < maxButtons - 1) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }
                  
                  const pageButtons = [];
                  
                  if (startPage > 1) {
                    pageButtons.push(
                      <button
                        key={1}
                        onClick={() => setPage(1)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pageButtons.push(
                        <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          i === page
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageButtons.push(
                        <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                    pageButtons.push(
                      <button
                        key={totalPages}
                        onClick={() => setPage(totalPages)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  
                  return pageButtons;
                })()}
                
                <button
                  onClick={() => setPage(Math.min(commissionData.pages, page + 1))}
                  disabled={page === commissionData.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">다음</span>
                  ▶
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* 메모 입력 모달 */}
      {showMemoModal && selectedCommission && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">
              {memoData.contractorNotes.includes('취소') ? '입금 확인 취소' : '입금 확인'}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {selectedCommission.contractor_name} 소장 - {formatCurrency(selectedCommission.commission_amount)}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                소장에게 보이는 메모 (선택사항)
              </label>
              <textarea
                value={memoData.contractorNotes}
                onChange={(e) => setMemoData({ ...memoData, contractorNotes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="소장이 확인할 수 있는 메모입니다."
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                관리자 전용 메모 (선택사항)
              </label>
              <textarea
                value={memoData.adminPrivateNotes}
                onChange={(e) => setMemoData({ ...memoData, adminPrivateNotes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="관리자만 볼 수 있는 메모입니다."
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleMemoSubmit(memoData.contractorNotes.includes('취소') ? 'cancelled' : 'paid')}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                확인
              </button>
              <button
                onClick={() => {
                  setShowMemoModal(false);
                  setSelectedCommission(null);
                  setMemoData({ contractorNotes: '', adminPrivateNotes: '' });
                }}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 월정액 수수료 생성 모달 */}
      {showMonthlyModal && selectedMonthlyContractor && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">월정액 수수료 생성</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                {selectedMonthlyContractor.name} 소장님의 {new Date().getMonth() + 1}월 정액 수수료를 생성하시겠습니까?
              </p>
              <p className="text-sm font-medium mt-2">
                금액: {formatCurrency(selectedMonthlyContractor.monthly_amount)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                납부일: 매월 {selectedMonthlyContractor.payment_day}일
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
              <p className="text-xs text-yellow-800">
                <strong>주의:</strong> 이미 이번 달 수수료가 생성되어 있다면 중복 생성될 수 있습니다.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => createMonthlyCommissionMutation.mutate(selectedMonthlyContractor.id)}
                disabled={createMonthlyCommissionMutation.isLoading}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {createMonthlyCommissionMutation.isLoading ? '생성 중...' : '생성'}
              </button>
              <button
                onClick={() => {
                  setShowMonthlyModal(false);
                  setSelectedMonthlyContractor(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCommissionHistory;